/**
 * Location Service - API methods for location management
 */

import apiClient from './apiClient';

export interface Location {
  id: string;
  organizationId: string;
  name: string;
  city: string;
  state: string;
  country: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  street?: string;
  zip?: string;
  phone?: string;
  createdById?: string;
  clinicianCount?: number;
  clientCount?: number;
}

export interface LocationDetail extends Location {
  createdBy?: { id: string; firstName: string; lastName: string };
  clinicianCount: number;
  clientCount: number;
}

export interface RosterClinician {
  id: string; // OrganizationMember id
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: { id: string; name: string }[];
  supervisorName?: string;
}

export interface RosterClient {
  id: string; // ClientMember id
  firstName: string;
  lastName: string;
  age?: number;
  assigneeName?: string;
}

export interface ChangeLogEntry {
  id: string;
  action: string;
  details: { fieldDiffs?: { field: string; oldValue: string; newValue: string }[] } | null;
  userId?: string;
  userName?: string;
  createdAt: string;
}

export interface DeactivatePreCheck {
  canDeactivate: boolean;
  blockers: { activeClinicians: number; activeClients: number };
}

export interface CreateLocationRequest {
  name: string;
  city: string;
  state: string;
  country: string;
  street?: string;
  zip?: string;
  phone?: string;
}

export interface UpdateLocationRequest {
  name?: string;
  city?: string;
  state?: string;
  country?: string;
  street?: string;
  zip?: string;
  phone?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface BulkLocationValidateRow {
  row: number;
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  valid: boolean;
  reason?: string;
}

export interface BulkLocationValidateResponse {
  rows: BulkLocationValidateRow[];
  validCount: number;
  invalidCount: number;
  totalCount: number;
}

export interface BulkLocationExecuteResponse {
  created: number;
  skipped: number;
}

interface BulkLocationValidateApiRow {
  rowNumber: number;
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  status: 'valid' | 'invalid';
  reason?: string;
}

interface BulkLocationValidateApiResponse {
  valid: BulkLocationValidateApiRow[];
  invalid: BulkLocationValidateApiRow[];
  totalRows: number;
  validCount: number;
  invalidCount: number;
}

const mapBulkLocationRow = (row: BulkLocationValidateApiRow): BulkLocationValidateRow => ({
  row: row.rowNumber,
  name: row.name,
  street: row.street,
  city: row.city,
  state: row.state,
  zip: row.zip,
  country: row.country,
  phone: row.phone,
  valid: row.status === 'valid',
  reason: row.reason,
});

export const getLocations = async (
  params: { page?: number; limit?: number; search?: string; status?: string } = {},
): Promise<PaginatedResponse<Location>> => {
  const response = await apiClient.get<{ success: boolean } & PaginatedResponse<Location>>(
    '/api/v1/locations',
    { params },
  );
  return { data: response.data.data, meta: response.data.meta };
};

export const createLocation = async (data: CreateLocationRequest): Promise<Location> => {
  const response = await apiClient.post<{ success: boolean; data: Location }>(
    '/api/v1/locations',
    data,
  );
  return response.data.data;
};

export const updateLocation = async (id: string, data: UpdateLocationRequest): Promise<Location> => {
  const response = await apiClient.patch<{ success: boolean; data: Location }>(
    `/api/v1/locations/${id}`,
    data,
  );
  return response.data.data;
};

export const deleteLocation = async (id: string): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ success: boolean; message: string }>(
    `/api/v1/locations/${id}`,
  );
  return { message: response.data.message };
};

export const getLocationDetail = async (id: string): Promise<LocationDetail> => {
  const response = await apiClient.get<{ success: boolean; data: LocationDetail }>(
    `/api/v1/locations/${id}`,
  );
  return response.data.data;
};

export const getLocationRoster = async (
  id: string,
  params: { page?: number; limit?: number; type: 'clinician' | 'client'; search?: string },
): Promise<PaginatedResponse<RosterClinician | RosterClient>> => {
  const response = await apiClient.get<
    { success: boolean } & PaginatedResponse<RosterClinician | RosterClient>
  >(`/api/v1/locations/${id}/roster`, { params });
  return { data: response.data.data, meta: response.data.meta };
};

export const getLocationChangelog = async (
  id: string,
  params: { page?: number; limit?: number },
): Promise<PaginatedResponse<ChangeLogEntry>> => {
  const response = await apiClient.get<
    { success: boolean } & PaginatedResponse<ChangeLogEntry>
  >(`/api/v1/locations/${id}/changelog`, { params });
  return { data: response.data.data, meta: response.data.meta };
};

export const getDeactivatePreflight = async (id: string): Promise<DeactivatePreCheck> => {
  const response = await apiClient.get<{ success: boolean; data: DeactivatePreCheck }>(
    `/api/v1/locations/${id}/deactivate-preflight`,
  );
  return response.data.data;
};

export const deactivateLocation = async (id: string): Promise<Location> => {
  const response = await apiClient.post<{ success: boolean; data: Location }>(
    `/api/v1/locations/${id}/deactivate`,
  );
  return response.data.data;
};

export const bulkValidateLocations = async (file: File): Promise<BulkLocationValidateResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<
    { success: boolean; data: BulkLocationValidateApiResponse }
  >('/api/v1/locations/bulk/validate', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const rows = [...response.data.data.valid, ...response.data.data.invalid]
    .sort((a, b) => a.rowNumber - b.rowNumber)
    .map(mapBulkLocationRow);

  return {
    rows,
    validCount: response.data.data.validCount,
    invalidCount: response.data.data.invalidCount,
    totalCount: response.data.data.totalRows,
  };
};

export const bulkExecuteLocations = async (
  rows: BulkLocationValidateRow[],
): Promise<BulkLocationExecuteResponse> => {
  const payloadRows = rows.map((row) => ({
    name: row.name ?? '',
    street: row.street,
    city: row.city ?? '',
    state: row.state ?? '',
    zip: row.zip,
    country: row.country ?? '',
    phone: row.phone,
  }));

  const response = await apiClient.post<{ success: boolean; data: BulkLocationExecuteResponse }>(
    '/api/v1/locations/bulk/execute',
    { rows: payloadRows },
  );

  return response.data.data;
};

const locationService = {
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  getLocationDetail,
  getLocationRoster,
  getLocationChangelog,
  getDeactivatePreflight,
  deactivateLocation,
  bulkValidateLocations,
  bulkExecuteLocations,
};

export default locationService;
